import React, { useCallback, useState } from "react";
import "./OrderDetails.scss";
import {
  Button,
  Divider,
  Steps,
  StepsProps,
  Image,
  Dropdown,
  MenuProps,
  Modal,
  Input,
  Rate,
  Upload,
  Form,
} from "antd";
import { DownOutlined, StarFilled, PlusOutlined, CameraOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd";
import locationIcon from "@assets/icons/location-marker.svg";
import contactIcon from "@assets/icons/contact.svg";
import calendarIcon from "@assets/icons/calendar.svg";
import clockIcon from "@assets/icons/clock.svg";
import truckIcon from "@assets/icons/truck.svg";
import mastercard from "@assets/icons/mastercard.svg";
import { ReactComponent as OrderIcon } from "@assets/icons/orderStep.svg";
import { ReactComponent as PlacedIcon } from "@assets/icons/placedStep.svg";
import { ReactComponent as ShippingIcon } from "@assets/icons/shippingStep.svg";
import { ReactComponent as ConfirmIcon } from "@assets/icons/confirmStep.svg";
import { ReactComponent as CompleteIcon } from "@assets/icons/completeStep.svg";
import { useUnit } from "contexts/UnitContext";
import { useUser } from "contexts/UserContext";
import Helpers from "@utils/Helpers";
import { reportApi, ISSUE_TYPE_MAP } from "services/ReportApi";
import orderActionsApi from "services/OrderActionsApi";
import { feedbackApi } from "services/FeedbackApi";

const OrderDetails: React.FC<any> = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const data = JSON.parse(urlParams.get("data")!);
  const [isShowProgress, setIsShowProgress] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState("");
  const [issueMessage, setIssueMessage] = useState("");
  const [isSubmittingIssue, setIsSubmittingIssue] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewImages, setReviewImages] = useState<UploadFile[]>([]);
  const { units } = useUnit();
  const { userProfile, userAddresses } = useUser();

  const orderId = data?.order?.order_id;
  const transactionStage = data?.transaction_status?.stage;
  const isReceived =
    data?.transaction_status?.stage >= 5 || data?.order?.received === true;

  const customDot: StepsProps["progressDot"] = (dot, { status, index }) => {
    return (
      <div className={`transaction-step-dot ${status}`}>
        {index === 0 && <PlacedIcon />}
        {index === 1 && <ConfirmIcon />}
        {index === 2 && <OrderIcon />}
        {index === 3 && <ShippingIcon />}
        {index === 4 && <CompleteIcon />}
      </div>
    );
  };

  const status: StepsProps["items"] = [
    {
      title: "Order Placed",
      description: "2025-02-18, 1:00 PM",
    },
    {
      title: "Confirmed",
      description: "2025-02-18, 1:00 PM",
    },
    {
      title: "Processing",
      description: "2025-02-18, 1:00 PM",
    },
    {
      title: "Shipping & Delivery",
      description: "2025-02-18, 1:00 PM",
    },
    {
      title: "Completion",
      description: "2025-02-18, 1:00 PM",
    },
  ];

  const items: MenuProps["items"] = Object.keys(ISSUE_TYPE_MAP).map(
    (label) => ({
      label,
      key: label,
      onClick: () => {
        setSelectedIssue(ISSUE_TYPE_MAP[label]);
        setIsReportOpen(true);
      },
    })
  );

  const handleCancelOrder = () => {
    Modal.confirm({
      title: "Cancel Order",
      content: "Are you sure you want to cancel this order?",
      onOk: async () => {
        try {
          await orderActionsApi.cancelOrder(orderId);
          Helpers.notification.success("Order cancelled successfully");
        } catch (error) {
          Helpers.notification.error("Failed to cancel order");
        }
      },
    });
  };

  const handleSubmitIssue = async () => {
    if (!issueMessage.trim()) {
      Helpers.notification.warning("Please describe the issue");
      return;
    }
    setIsSubmittingIssue(true);
    try {
      await reportApi.reportOrderIssue(orderId, selectedIssue, issueMessage);
      Helpers.notification.success("Issue reported successfully");
      setIsReportOpen(false);
      setIssueMessage("");
    } catch (error) {
      Helpers.notification.error("Failed to report issue");
    } finally {
      setIsSubmittingIssue(false);
    }
  };

  const handleSubmitReview = async () => {
    const productId =
      data?.product_items?.[0]?.product_id ??
      data?.order?.product_id ??
      data?.product_id ??
      705;
    if (!productId) {
      Helpers.notification.error("Missing product information");
      return;
    }
    setIsSubmittingReview(true);
    try {
      await feedbackApi.postProductFeedback({
        product_id: productId,
        rating: reviewRating,
        comment: reviewComment,
      });
      Helpers.notification.success("Review submitted successfully");
      setIsReviewOpen(false);
      setReviewComment("");
      setReviewRating(5);
      setReviewImages([]);
    } catch (error) {
      Helpers.notification.error("Failed to submit review");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const progressItem = [
    {
      title: (
        <>
          <span>2025-02-18</span> <span>1:00 PM</span>
        </>
      ),
      description: "Order placed successfully.",
    },
    {
      title: (
        <>
          <span>2025-02-18</span> <span>5:00 PM</span>
        </>
      ),
      description: "Order confirmed.",
    },
    {
      title: "In Progress",
      description: "This is a description. This is a description.",
    },
    {
      title: "Waiting",
      description: "This is a description.",
    },
    {
      title: "Waiting",
      description: "This is a description.",
    },
  ];

  const visibleItems = isShowProgress ? progressItem : progressItem.slice(0, 2);

  const getUserAddress = useCallback(() => {
    if (!userProfile?.address_id) return;
    const address = userAddresses.find(
      (item: any) => item.address_id === userProfile?.address_id
    );
    return address;
  }, [userProfile?.address_id]);

  return (
    <div className="OrderDetails">
      <div className="container">
        <div className="title">Order Details</div>
        <div className="detail">
          <div className="header">
            <div className="header-info">
              <div className="farm-info">
                <div className="farm-info-group">
                  <div className="farm-name">
                    {data?.producer_farm?.producer_name}{" "}
                    <span className="rating">
                      <StarFilled /> 4.5
                    </span>
                  </div>
                  <div className="farm-location">
                    <img src={locationIcon} width={15} alt="location" />
                    <span>{data?.producer_farm?.province}</span>
                  </div>
                </div>
                <Button
                  className="message-btn"
                  icon={<img src={contactIcon} alt="contact" />}
                />
              </div>
              <div className="total-price">
                Total:
                <br />${Helpers.formatNumber(data?.order.total_price, 2)}
              </div>
              <div className="ship-to">
                Ship to:
                <br />{" "}
                {userProfile?.user_id === data?.order.consumer_id
                  ? userProfile?.first_name + " " + userProfile?.last_name
                  : ""}
              </div>
              <div className="order-id">
                Order ID:
                <br /> #{data?.order.order_id}
              </div>
              <Button type="link" className="invoice-btn">
                Invoice
              </Button>
            </div>
          </div>
          <div className="order-delivery">
            <div className="delivery-info">
              <div className="shipping-mode">
                <img src={truckIcon} width={17} alt="truck" />
                <span>
                  {data?.producer_farm.shipping_method
                    .split(" ")
                    .map(
                      (word: any) =>
                        word.charAt(0).toUpperCase() + word.slice(1)
                    )
                    .join(" ")}
                </span>
              </div>
              <div className="shipping-date">
                <img src={calendarIcon} width={17} alt="calendar" />
                <span>Dec 12 - Dec 16</span>
              </div>
              <div className="shipping-distance">
                <img src={locationIcon} width={17} alt="location" />
                <span>264km</span>
              </div>
              <div className="shipping-time">
                <img src={clockIcon} width={17} alt="clock" />
                <span>3h 22m</span>
              </div>
            </div>
          </div>
          <div className="order-step">
            <Steps
              current={data?.transaction_status.stage - 1}
              progressDot={customDot}
              items={status}
            />
          </div>
          <div className="order-info">
            <div className="address">
              <div className="section-title">Shipping Address:</div>
              <div className="address-detail">
                <span className="name">
                  {userProfile?.user_id === data?.order.consumer_id
                    ? userProfile?.first_name + " " + userProfile?.last_name
                    : ""}
                </span>
                <span className="phone">
                  {userProfile?.user_id === data?.order.consumer_id
                    ? userProfile?.phone
                    : ""}
                </span>
                <span className="address">
                  {getUserAddress()?.user_id === data?.order.consumer_id
                    ? getUserAddress()?.street +
                      " " +
                      getUserAddress()?.apartment +
                      " " +
                      getUserAddress()?.city +
                      " " +
                      getUserAddress()?.province
                    : ""}
                </span>
              </div>
            </div>
            <div className="progress">
              <Steps
                progressDot
                current={1}
                direction="vertical"
                items={visibleItems}
              />

              {!isShowProgress ? (
                <Button
                  className="see-more-btn"
                  type="link"
                  onClick={() => setIsShowProgress(!isShowProgress)}
                >
                  See More
                </Button>
              ) : (
                <Button
                  className="see-more-btn"
                  type="link"
                  onClick={() => setIsShowProgress(!isShowProgress)}
                >
                  See Less
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="detail">
          <div className="payment">
            <div className="payment-method">
              <b>Payment Method:</b>
              <span>
                <img src={mastercard} width={36} alt="mastercard" /> **** ****
                **** 1075
              </span>
            </div>
            <div className="payment-summary">
              <b>Summary:</b>
              <div className="summary-group">
                <div className="summary-item">
                  <span>2 items:</span>
                  <span>$2,000.00</span>
                </div>
                <div className="summary-item">
                  <span>Estimated tax:</span>
                  <span>$200.00</span>
                </div>
                <div className="summary-item">
                  <span>Transaction fee:</span>
                  <span>$40.00</span>
                </div>
                <div className="summary-item">
                  <span>Shipping & handling:</span>
                  <span>$200.00</span>
                </div>
              </div>
              <Divider />
              <div className="total">
                <b>Total:</b>
                <b>$2,440.00</b>
              </div>
            </div>
          </div>
        </div>

        <div className="detail">
          <div className="product">
            <Image
              className="product-image"
              src="https://upload.wikimedia.org/wikipedia/commons/4/4c/Roundbale1.jpg"
              width={120}
              height={100}
              preview={false}
            />
            <div className="product-info">
              <div className="product-name">
                <b>Alfalfa</b> <div className="product-type">Hay</div>
              </div>
              <div className="checkout-info">
                <div className="product-quantity">
                  <div className="label">Quantity:</div>
                  <span>10 Bales</span>
                </div>
                <div className="product-price">
                  $ 3.25 / Ton
                  <b>$799.79</b>
                </div>
              </div>
            </div>
          </div>

          <Divider />

          <div className="product">
            <Image
              className="product-image"
              src="https://upload.wikimedia.org/wikipedia/commons/4/49/Bales_from_corn_stems_01.jpg"
              width={120}
              height={100}
              preview={false}
            />
            <div className="product-info">
              <div className="product-name">
                <b>Alfalfa</b> <div className="product-type">Hay</div>
              </div>
              <div className="checkout-info">
                <div className="product-quantity">
                  <div className="label">Quantity:</div>
                  <span>10 Bales</span>
                </div>
                <div className="product-price">
                  $ 3.25 / Ton
                  <b>$799.79 </b>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="btn-group">
          {transactionStage !== 4 && transactionStage !== 5 && (
            <div>
              <Button className="cancel-btn" type="link" onClick={handleCancelOrder}>
                Cancel Order
              </Button>
            </div>
          )}
          <div className="report-review">
            <Dropdown menu={{ items }} trigger={["click"]}>
              <Button className="report-btn" type="link">
                Report an Issue
                <DownOutlined />
              </Button>
            </Dropdown>

            <Button
              className="review-btn"
              type="link"
              onClick={() => setIsReviewOpen(true)}
            >
              Write Review
            </Button>
          </div>
        </div>

        <Modal
          title="Report an Issue"
          open={isReportOpen}
          onCancel={() => setIsReportOpen(false)}
          onOk={handleSubmitIssue}
          confirmLoading={isSubmittingIssue}
        >
          <p>Issue type: {selectedIssue}</p>
          <Input.TextArea
            rows={4}
            value={issueMessage}
            onChange={(e) => setIssueMessage(e.target.value)}
            placeholder="Describe the issue"
          />
        </Modal>

        <Modal
          title="Write a Review"
          open={isReviewOpen}
          onCancel={() => setIsReviewOpen(false)}
          onOk={handleSubmitReview}
          confirmLoading={isSubmittingReview}
          width={520}
        >
          <div style={{ marginBottom: 12 }}>
            <Rate value={reviewRating} onChange={setReviewRating} allowHalf />
          </div>
          <Input.TextArea
            rows={4}
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
            placeholder="Share your experience with this product..."
            maxLength={2000}
            showCount
          />
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Add photos (optional)</div>
            <Upload
              listType="picture-card"
              fileList={reviewImages}
              beforeUpload={() => false}
              onChange={({ fileList }) => setReviewImages(fileList)}
              accept="image/*"
              multiple
              showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
            >
              {reviewImages.length >= 5 ? null : (
                <div className="review-upload-trigger">
                  <CameraOutlined style={{ fontSize: 28, color: "#999" }} />
                  <div style={{ marginTop: 8, color: "#666", fontSize: 13 }}>
                    Add photo
                  </div>
                </div>
              )}
            </Upload>
            <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
              Images are preview-only for now; the backend does not store review images yet.
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default OrderDetails;
